import { z } from 'zod';
import { hexColor, objectIdStr } from './common.js';

/**
 * Routing order, ascending (SPEC §2). `INSTALLATION` is a real station here — four
 * installers already exist in `workers` pointing at it, and their capacity went
 * nowhere in the old data because no station matched.
 *
 * `SHIPPING_RECEIVING` is the first scan-only station: it is scanned against but never
 * planned. See `scanOnly` below.
 */
export const STATION_KEYS = [
  'ORDER_CREATION',
  'NESTING',
  'CNC',
  'CARPENTRY',
  'FINISHING_SAND',
  'FINISHING_PAINT',
  'ASSEMBLING',
  'DELIVERY',
  'INSTALLATION',
  'SHIPPING_RECEIVING',
] as const;

export type StationKey = (typeof STATION_KEYS)[number];

export const stationKey = z.enum(STATION_KEYS);

/**
 * Last-resort display text, derived from the key: `FINISHING_SAND` → "Finishing Sand".
 *
 * A station's real name lives on its document and is the shop's to change, so the UI
 * should show `station.name`. This is only for a key with no station behind it — one
 * the shop has deleted, still referenced by an old booking or scan.
 */
export function stationLabel(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

export const station = z.object({
  _id: objectIdStr,
  key: stationKey,
  name: z.string().min(1),
  color: hexColor,
  /** Ascending routing order. */
  order: z.number().int(),
  /**
   * Set for stations constrained by a machine rather than headcount — a single CNC
   * is 8h/day no matter how many operators are listed against it. Wins over the
   * worker sum when present.
   */
  machineCapacityHours: z.number().min(0).optional(),
  /**
   * Recorded against, never planned: a receiving desk, a QC spot-check. It carries no
   * capacity, never appears on the board, and is refused in a routing — but it is
   * offered on the Scan page like any other station, and its scans show up in a job's
   * scan history.
   *
   * Evidence from one is deliberately inert: `confirmedPartsByStation` ignores a scan
   * at a station outside the batch's routing, so arriving at Receiving never implies
   * the part cleared CNC.
   */
  scanOnly: z.boolean().default(false),
});
export type Station = z.infer<typeof station>;

/** `scanOnly` is partial like `color`/`order`: a write that omits it leaves the stored
 * value alone rather than silently un-marking a scan-only station. */
export const stationWrite = station
  .omit({ _id: true })
  .partial({ color: true, order: true, scanOnly: true });
export type StationWrite = z.infer<typeof stationWrite>;

/** Seed values — the shop's fixed routing, with DELIVERY no longer sorted first.
 * `scanOnly` is omitted on the scheduled ones and defaulted on read. */
export const DEFAULT_STATIONS: (Omit<Station, '_id' | 'scanOnly'> & { scanOnly?: boolean })[] = [
  { key: 'ORDER_CREATION', name: 'Order creation', color: '#6fb4bd', order: 10 },
  { key: 'NESTING', name: 'Nesting', color: '#e6b8d4', order: 20 },
  { key: 'CNC', name: 'CNC', color: '#ebd3da', order: 30, machineCapacityHours: 8 },
  { key: 'CARPENTRY', name: 'Carpentry', color: '#d0d6a5', order: 40 },
  { key: 'FINISHING_SAND', name: 'Finishing Parts', color: '#bae3c6', order: 50 },
  { key: 'FINISHING_PAINT', name: 'Finishing Doors', color: '#6fa4a6', order: 60 },
  { key: 'ASSEMBLING', name: 'Assembling', color: '#a6a46f', order: 70 },
  { key: 'DELIVERY', name: 'Delivery', color: '#a7c5d6', order: 80 },
  { key: 'INSTALLATION', name: 'Installation', color: '#c8b6d6', order: 90 },
  // Sorted last: order ranks the routing, and this one is not in it.
  {
    key: 'SHIPPING_RECEIVING',
    name: 'Shipping & Receiving',
    color: '#d6c3a7',
    order: 100,
    scanOnly: true,
  },
];
